import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useCan, useMe } from "@/lib/authz";
import ScopeFilterBar, { scopeToQuery } from "@/components/ScopeFilterBar";
import {
  Plus, EnvelopeSimple, Video, ClipboardText, XCircle, CalendarX, Clock, CheckCircle,
  ArrowClockwise,
} from "@phosphor-icons/react";
import { useMasterData } from "@/hooks/useMasterData";

const STATUS_CLS = {
  Scheduled: "bg-sky-50 text-sky-700 border-sky-200",
  Completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Cancelled: "bg-slate-100 text-slate-500 border-slate-200",
  "No Show": "bg-red-50 text-red-700 border-red-200",
};

const emptyForm = {
  candidate_id: "", evaluation_date: new Date().toISOString().slice(0, 10),
  evaluation_time: "10:00", duration_minutes: 45, timezone: "America/New_York",
  meeting_platform: "Zoom", meeting_link: "", cc_emails: "",
};

const TIMEZONES = ["America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles", "Asia/Kolkata", "Europe/London", "Australia/Sydney"];

const emptyEvalForm = {
  primary_skill: "", secondary_skill: "", years_experience: "",
  salary_expectation: "", notice_period: "",
  communication_rating: 3, resume_rating: 3, linkedin_rating: 3,
  strengths: "", weaknesses: "", overall_evaluation: "",
  job_search_strategy: "", internal_notes: "", recording_url: "",
};

export default function Evaluations() {
  const { user } = useAuth();
  const { canSeeOwner } = useMe();
  // Phase A/B/C/D — Evaluations require candidate.edit OR evaluation.manage
  // permission. GL/Manager retain supervisory access.
  const hasCandEdit = useCan("candidate.edit");
  const hasEvalPerm = useCan("evaluation.manage");
  const canRun = hasCandEdit || hasEvalPerm ||
    ["admin", "super_admin", "team_lead", "sdl"].includes(user?.role);
  // Row-level edit gate: matches backend PATCH /evaluations/{eid} rule.
  const canEditRow = (ev) => canRun && (
    ["admin", "super_admin"].includes(user?.role) ||
    canSeeOwner(ev.employee_id || ev.assigned_employee_id)
  );
  const [items, setItems] = useState([]);
  const [cands, setCands] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [formOpen, setFormOpen] = useState(null);
  const [evalForm, setEvalForm] = useState(emptyEvalForm);
  const [timelineFor, setTimelineFor] = useState(null); // evaluation obj
  const [events, setEvents] = useState([]);
  const [rescheduleFor, setRescheduleFor] = useState(null);
  const [rescheduleForm, setRescheduleForm] = useState({});
  const [filters, setFilters] = useState({});

  const platforms = useMasterData("evaluation_platform").items;

  const load = useCallback(() => {
    if (!canRun) return Promise.resolve();
    const p = new URLSearchParams(scopeToQuery(filters)).toString();
    return api.get(`/evaluations${p ? `?${p}` : ""}`).then((r) => setItems(r.data));
  }, [canRun, filters]);
  useEffect(() => {
    if (!canRun) return;
    load();
    api.get("/candidates").then((r) => setCands(r.data));
  }, [load, canRun]);

  if (!canRun) {
    return (
      <div className="max-w-lg mx-auto mt-16 border border-slate-200 rounded-lg p-6 text-center bg-white" data-testid="evaluations-forbidden">
        <div className="text-lg font-semibold text-slate-800">Evaluations</div>
        <p className="text-sm text-slate-500 mt-2">This module is only available to Administrators, Managers and Group Leads.</p>
      </div>
    );
  }

  const create = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form,
        cc_emails: form.cc_emails ? form.cc_emails.split(",").map((s) => s.trim()).filter(Boolean) : [],
        duration_minutes: Number(form.duration_minutes) || 45,
      };
      await api.post("/evaluations", payload);
      toast.success("Evaluation scheduled");
      setOpen(false); setForm(emptyForm); load();
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  const sendInvite = async (id) => {
    try {
      const r = await api.post(`/evaluations/${id}/send-invite`, { instructions: "Please have your resume, LinkedIn profile, and 2 sample job descriptions ready." });
      if (r.data.ok) toast.success("Invite sent");
      else toast.error(r.data.error || "Failed to send");
      load();
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  const openFormFor = (ev) => { setFormOpen(ev.id); setEvalForm(ev.form || emptyEvalForm); };

  const submitForm = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/evaluations/${formOpen}/form`, evalForm);
      toast.success("Evaluation form saved"); setFormOpen(null); load();
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  const doAction = async (id, action) => {
    let reason = "";
    if (action === "cancel" || action === "no-show") {
      reason = window.prompt(`Reason for ${action}?`) || "";
    }
    try {
      await api.post(`/evaluations/${id}/${action}`, { reason, notes: reason });
      toast.success(`Marked ${action}`); load();
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  const openTimeline = async (ev) => {
    setTimelineFor(ev);
    try { const { data } = await api.get(`/evaluations/${ev.id}/events`); setEvents(data || []); }
    catch { setEvents([]); }
  };

  const openReschedule = (ev) => {
    setRescheduleFor(ev);
    setRescheduleForm({
      evaluation_date: ev.evaluation_date, evaluation_time: ev.evaluation_time,
      duration_minutes: ev.duration_minutes || 45, timezone: ev.timezone || "America/New_York",
      meeting_platform: ev.meeting_platform, meeting_link: ev.meeting_link || "",
      cc_emails: (ev.cc_emails || []).join(", "), reason: "",
    });
  };

  const submitReschedule = async (e) => {
    e.preventDefault();
    if (!rescheduleFor) return;
    try {
      const payload = { ...rescheduleForm,
        duration_minutes: Number(rescheduleForm.duration_minutes) || 45,
        cc_emails: rescheduleForm.cc_emails ? rescheduleForm.cc_emails.split(",").map((s) => s.trim()).filter(Boolean) : [],
      };
      await api.patch(`/evaluations/${rescheduleFor.id}`, payload);
      toast.success("Rescheduled"); setRescheduleFor(null); load();
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5" data-testid="evaluations-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Evaluations</h2>
          <p className="text-sm text-slate-500">Schedule, reschedule, cancel, mark no-show and complete with full audit timeline.</p>
        </div>
        <button onClick={() => setOpen(true)} data-testid="new-evaluation-button"
                className="bg-sky-600 hover:bg-sky-700 text-white text-sm px-4 py-2 rounded-md flex items-center gap-2">
          <Plus size={16} /> New Evaluation
        </button>
      </div>

      <ScopeFilterBar value={filters} onChange={setFilters}
                      candidates={cands}
                      showStatus statusOptions={["Scheduled", "Rescheduled", "Cancelled", "No Show", "Completed"]}
                      statusLabel="Evaluation Status"
                      testid="evaluations-scope" />

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase text-xs tracking-widest">
            <tr>
              <th className="text-left px-4 py-3">Candidate</th>
              <th className="text-left px-4 py-3">When</th>
              <th className="text-left px-4 py-3">Platform</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Invite</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((ev) => {
              const c = cands.find((x) => x.id === ev.candidate_id);
              const cls = STATUS_CLS[ev.status] || "bg-slate-50 text-slate-700 border-slate-200";
              return (
                <tr key={ev.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3">{c?.full_name || "—"}</td>
                  <td className="px-4 py-3 text-xs">
                    <div>{ev.evaluation_date} · {ev.evaluation_time}</div>
                    <div className="text-slate-500">{ev.duration_minutes || 45}min · {ev.timezone || "America/New_York"}</div>
                  </td>
                  <td className="px-4 py-3 inline-flex items-center gap-1"><Video size={14} /> {ev.meeting_platform}</td>
                  <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full border ${cls}`}>{ev.status}</span></td>
                  <td className="px-4 py-3 text-xs">
                    {ev.invite_sent_at ? <span className="text-emerald-700">Sent {new Date(ev.invite_sent_at).toLocaleString()}</span> : <span className="text-slate-400">Not sent</span>}
                  </td>
                  <td className="px-4 py-3 text-right space-x-1">
                    {ev.status === "Scheduled" && <>
                      <button onClick={() => sendInvite(ev.id)} data-testid={`send-invite-${ev.id}`} title="Send invite" className="text-sky-700 hover:text-sky-900 p-1"><EnvelopeSimple size={16} /></button>
                      <button onClick={() => openReschedule(ev)} data-testid={`reschedule-${ev.id}`} title="Reschedule" className="text-amber-700 hover:text-amber-900 p-1"><ArrowClockwise size={16} /></button>
                      <button onClick={() => doAction(ev.id, "cancel")} data-testid={`cancel-${ev.id}`} title="Cancel" className="text-red-600 hover:text-red-800 p-1"><XCircle size={16} /></button>
                      <button onClick={() => doAction(ev.id, "no-show")} data-testid={`noshow-${ev.id}`} title="No show" className="text-slate-500 hover:text-slate-800 p-1"><CalendarX size={16} /></button>
                      <button onClick={() => doAction(ev.id, "complete")} data-testid={`complete-${ev.id}`} title="Complete" className="text-emerald-700 hover:text-emerald-900 p-1"><CheckCircle size={16} /></button>
                    </>}
                    <button onClick={() => openFormFor(ev)} data-testid={`open-form-${ev.id}`} title="Evaluation form" className="text-slate-700 hover:text-slate-900 p-1"><ClipboardText size={16} /></button>
                    <button onClick={() => openTimeline(ev)} data-testid={`timeline-${ev.id}`} title="Timeline" className="text-slate-500 hover:text-slate-800 p-1"><Clock size={16} /></button>
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && <tr><td colSpan={6} className="text-center text-slate-400 py-10">No evaluations scheduled.</td></tr>}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm grid place-items-center p-4 z-50">
          <form onSubmit={create} className="bg-white border border-slate-200 rounded-xl p-6 w-full max-w-2xl" data-testid="new-evaluation-form">
            <h3 className="font-semibold mb-4">Schedule Evaluation</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <label className="text-xs text-slate-500">Candidate</label>
                <select required value={form.candidate_id} onChange={(e) => setForm((f) => ({ ...f, candidate_id: e.target.value }))} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm">
                  <option value="">Select…</option>
                  {cands.map((c) => <option key={c.id} value={c.id}>{c.full_name}</option>)}
                </select>
              </div>
              <div><label className="text-xs text-slate-500">Date</label><input required type="date" value={form.evaluation_date} onChange={(e) => setForm((f) => ({ ...f, evaluation_date: e.target.value }))} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" /></div>
              <div><label className="text-xs text-slate-500">Time</label><input required type="time" value={form.evaluation_time} onChange={(e) => setForm((f) => ({ ...f, evaluation_time: e.target.value }))} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" /></div>
              <div><label className="text-xs text-slate-500">Duration (min)</label><input type="number" value={form.duration_minutes} onChange={(e) => setForm((f) => ({ ...f, duration_minutes: e.target.value }))} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" /></div>
              <div><label className="text-xs text-slate-500">Timezone</label>
                <select value={form.timezone} onChange={(e) => setForm((f) => ({ ...f, timezone: e.target.value }))} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm">
                  {TIMEZONES.map((tz) => <option key={tz}>{tz}</option>)}
                </select>
              </div>
              <div><label className="text-xs text-slate-500">Platform</label>
                <select value={form.meeting_platform} onChange={(e) => setForm((f) => ({ ...f, meeting_platform: e.target.value }))} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm">
                  {(platforms.length ? platforms.map((p) => p.value) : ["Zoom", "Google Meet", "Teams", "Other"]).map((p) => <option key={p}>{p}</option>)}
                </select>
              </div>
              <div className="md:col-span-1"><label className="text-xs text-slate-500">Meeting link</label><input value={form.meeting_link} onChange={(e) => setForm((f) => ({ ...f, meeting_link: e.target.value }))} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" /></div>
              <div className="md:col-span-2"><label className="text-xs text-slate-500">CC emails (comma-separated)</label><input value={form.cc_emails} onChange={(e) => setForm((f) => ({ ...f, cc_emails: e.target.value }))} placeholder="lead@kgs.com, admin@kgs.com" className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" /></div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 text-sm rounded-md border border-slate-200">Cancel</button>
              <button type="submit" data-testid="submit-new-eval" className="px-4 py-2 text-sm rounded-md bg-sky-600 hover:bg-sky-700 text-white">Schedule</button>
            </div>
          </form>
        </div>
      )}

      {rescheduleFor && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm grid place-items-center p-4 z-50">
          <form onSubmit={submitReschedule} className="bg-white border border-slate-200 rounded-xl p-6 w-full max-w-2xl" data-testid="reschedule-form">
            <h3 className="font-semibold mb-4">Reschedule Evaluation</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><label className="text-xs text-slate-500">Date</label><input type="date" value={rescheduleForm.evaluation_date} onChange={(e) => setRescheduleForm((f) => ({ ...f, evaluation_date: e.target.value }))} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" /></div>
              <div><label className="text-xs text-slate-500">Time</label><input type="time" value={rescheduleForm.evaluation_time} onChange={(e) => setRescheduleForm((f) => ({ ...f, evaluation_time: e.target.value }))} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" /></div>
              <div><label className="text-xs text-slate-500">Duration (min)</label><input type="number" value={rescheduleForm.duration_minutes} onChange={(e) => setRescheduleForm((f) => ({ ...f, duration_minutes: e.target.value }))} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" /></div>
              <div><label className="text-xs text-slate-500">Timezone</label>
                <select value={rescheduleForm.timezone} onChange={(e) => setRescheduleForm((f) => ({ ...f, timezone: e.target.value }))} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm">
                  {TIMEZONES.map((tz) => <option key={tz}>{tz}</option>)}
                </select>
              </div>
              <div className="md:col-span-2"><label className="text-xs text-slate-500">Meeting link</label><input value={rescheduleForm.meeting_link} onChange={(e) => setRescheduleForm((f) => ({ ...f, meeting_link: e.target.value }))} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" /></div>
              <div className="md:col-span-2"><label className="text-xs text-slate-500">CC emails</label><input value={rescheduleForm.cc_emails} onChange={(e) => setRescheduleForm((f) => ({ ...f, cc_emails: e.target.value }))} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" /></div>
              <div className="md:col-span-2"><label className="text-xs text-slate-500">Reason</label><input value={rescheduleForm.reason} onChange={(e) => setRescheduleForm((f) => ({ ...f, reason: e.target.value }))} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" /></div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button type="button" onClick={() => setRescheduleFor(null)} className="px-4 py-2 text-sm rounded-md border border-slate-200">Cancel</button>
              <button type="submit" data-testid="submit-reschedule" className="px-4 py-2 text-sm rounded-md bg-sky-600 hover:bg-sky-700 text-white">Save changes</button>
            </div>
          </form>
        </div>
      )}

      {timelineFor && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm grid place-items-center p-4 z-50">
          <div className="bg-white border border-slate-200 rounded-xl p-6 w-full max-w-lg max-h-[80vh] overflow-y-auto" data-testid="timeline-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">Timeline</h3>
              <button onClick={() => setTimelineFor(null)} className="text-slate-400">×</button>
            </div>
            <div className="space-y-2 text-sm">
              {events.length === 0 && <div className="text-slate-400 text-center py-6">No events yet.</div>}
              {events.map((e) => (
                <div key={e.id} className="border-l-2 border-sky-500 pl-3 py-1">
                  <div className="font-medium capitalize">{e.type.replace(/_/g, " ")}</div>
                  <div className="text-xs text-slate-500">{new Date(e.at).toLocaleString()} · by {e.by?.name || "system"}</div>
                  {e.meta?.reason && <div className="text-xs text-slate-600 mt-1">Reason: {e.meta.reason}</div>}
                  {e.meta?.notes && <div className="text-xs text-slate-600 mt-1">Notes: {e.meta.notes}</div>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {formOpen && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm grid place-items-center p-4 z-50">
          <form onSubmit={submitForm} className="bg-white border border-slate-200 rounded-xl p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <h3 className="font-semibold mb-4">Evaluation Form</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <TextField label="Primary Skill" value={evalForm.primary_skill} onChange={(v) => setEvalForm((f) => ({ ...f, primary_skill: v }))} />
              <TextField label="Secondary Skill" value={evalForm.secondary_skill} onChange={(v) => setEvalForm((f) => ({ ...f, secondary_skill: v }))} />
              <TextField label="Years of Experience" type="number" value={evalForm.years_experience} onChange={(v) => setEvalForm((f) => ({ ...f, years_experience: v }))} />
              <TextField label="Salary Expectation" value={evalForm.salary_expectation} onChange={(v) => setEvalForm((f) => ({ ...f, salary_expectation: v }))} />
              <TextField label="Notice Period" value={evalForm.notice_period} onChange={(v) => setEvalForm((f) => ({ ...f, notice_period: v }))} />
              <TextField label="Recording URL" value={evalForm.recording_url} onChange={(v) => setEvalForm((f) => ({ ...f, recording_url: v }))} />
              <RatingField label="Communication (1-5)" value={evalForm.communication_rating} onChange={(v) => setEvalForm((f) => ({ ...f, communication_rating: v }))} />
              <RatingField label="Resume (1-5)" value={evalForm.resume_rating} onChange={(v) => setEvalForm((f) => ({ ...f, resume_rating: v }))} />
              <RatingField label="LinkedIn (1-5)" value={evalForm.linkedin_rating} onChange={(v) => setEvalForm((f) => ({ ...f, linkedin_rating: v }))} />
              <TextAreaField label="Strengths" value={evalForm.strengths} onChange={(v) => setEvalForm((f) => ({ ...f, strengths: v }))} />
              <TextAreaField label="Weaknesses" value={evalForm.weaknesses} onChange={(v) => setEvalForm((f) => ({ ...f, weaknesses: v }))} />
              <TextAreaField label="Overall Evaluation" value={evalForm.overall_evaluation} onChange={(v) => setEvalForm((f) => ({ ...f, overall_evaluation: v }))} className="md:col-span-2" />
              <TextAreaField label="Job Search Strategy" value={evalForm.job_search_strategy} onChange={(v) => setEvalForm((f) => ({ ...f, job_search_strategy: v }))} className="md:col-span-2" />
              <TextAreaField label="Internal Notes" value={evalForm.internal_notes} onChange={(v) => setEvalForm((f) => ({ ...f, internal_notes: v }))} className="md:col-span-2" />
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button type="button" onClick={() => setFormOpen(null)} className="px-4 py-2 text-sm rounded-md border border-slate-200">Cancel</button>
              <button type="submit" data-testid="submit-eval-form" className="px-4 py-2 text-sm rounded-md bg-sky-600 hover:bg-sky-700 text-white">Save Form</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function TextField({ label, value, onChange, type = "text", className = "" }) {
  return (<div className={className}><label className="text-xs text-slate-500">{label}</label>
    <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" /></div>);
}
function TextAreaField({ label, value, onChange, className = "" }) {
  return (<div className={className}><label className="text-xs text-slate-500">{label}</label>
    <textarea rows={2} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm" /></div>);
}
function RatingField({ label, value, onChange }) {
  return (<div><label className="text-xs text-slate-500">{label}</label>
    <select value={value} onChange={(e) => onChange(Number(e.target.value))} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm">
      {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
    </select></div>);
}
